"use client";

import { NEWSLETTER_API } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { Dialog } from "@headlessui/react";
import { ArrowRightIcon, Loader2Icon, MailIcon, XIcon } from "lucide-react";
import Image from "next/image";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

const Popup = () => {
	const { isLoggedIn } = useAuth();

	const [showPopup, setShowPopup] = useState(false);

	const [email, setEmail] = useState<string>("");
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEmail(e.target.value);
		setErrorMessage("");
	};

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		try {
			setIsLoading(true);

			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!email.trim()) {
				setErrorMessage("Please enter an email");
				setIsLoading(false);
				return;
			}
			if (!emailRegex.test(email)) {
				setErrorMessage("Please enter a valid email");
				setIsLoading(false);
				return;
			}

			const url = new URL(NEWSLETTER_API);
			const response = await fetch(url, {
				method: "POST",
				body: JSON.stringify({ email }),
			});

			const data = await response.json();
			const message = data?.message;

			if (response.ok) {
				window.rudderanalytics?.track("Newsletter Signup", {
					email: email,
					fromSource: "News homepage popup",
				});

				toast.success(message);
			} else {
				toast.error(message);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setEmail("");
			setIsLoading(false);
		}
	};

	useEffect(() => {
		const timer = setTimeout(() => {
			setShowPopup(true);
		}, 5000);

		return () => clearTimeout(timer);
	}, []);

	if (isLoggedIn) {
		return null;
	}

	return (
		<Dialog
			as="div"
			className="fixed inset-0 z-30 overflow-y-auto"
			open={showPopup}
			onClose={() => setShowPopup(false)}
		>
			<Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

			<div className="absolute left-1/2 top-1/2 w-11/12 -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white shadow-lg lg:w-[668px]">
				<div className="flex flex-col">
					<div className="absolute w-full p-4 text-right">
						<button
							onClick={() => setShowPopup(false)}
							className="h-6 w-6 rounded-full bg-brand-primary p-1 text-white outline-none"
						>
							<XIcon className="h-full w-full object-contain" />
						</button>
					</div>

					<Image
						src={"/news/popup-thumbnail.png"}
						alt={"Subscribe"}
						width={1005}
						height={496}
						className="rounded-tl-lg rounded-tr-lg"
					/>

					<div className="bg-[url('/news/popup-bg.png')] bg-no-repeat">
						<div className="mx-auto flex max-w-xl flex-col items-center justify-center p-4 lg:px-16 lg:py-8">
							<div className="mb-8 space-y-2 text-center">
								<h3 className="text-xl font-bold text-gray-800">
									Proptech Newsletter that makes you smart
								</h3>
								<p className="text-gray-500">
									Over 5000 subscribers who are PropTech Buyers,
									<br /> Proptech Founders, and PropTech Investors
								</p>
							</div>

							<form id="newsletterForm" onSubmit={onSubmit}>
								<div className="mb-2 flex w-full items-center gap-2 rounded-lg border-2 border-gray-200 px-4">
									<MailIcon className="h-6 w-6 text-brand-primary" />

									<input
										placeholder="Enter your email here"
										className="w-full px-4 py-2 outline-none md:px-4 md:py-3"
										type="email"
										name="email"
										id="email"
										value={email}
										onChange={onChange}
										autoComplete="email"
										required
									/>
								</div>

								<button
									className="mt-2 w-full rounded-md bg-brand-primary px-3 py-2 text-white shadow-md md:px-4 md:py-3"
									disabled={isLoading}
								>
									{isLoading ? (
										<Loader2Icon className="h-4 w-4 animate-spin stroke-white" />
									) : (
										<>
											<div className="flex items-center justify-center gap-2">
												<span>Join Newsletter</span>

												<ArrowRightIcon className="h-4 w-4" />
											</div>
										</>
									)}
								</button>
							</form>
							<p
								className={cn(
									"text-sm text-red-400 transition-opacity duration-150 ease-linear",
									errorMessage ? "opacity-100" : "opacity-0"
								)}
							>
								{errorMessage}
							</p>
						</div>
					</div>
				</div>
			</div>
		</Dialog>
	);
};

export default Popup;
